import { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, Sparkles } from 'lucide-react';
import { fmtCurrency } from '@/utils/money';
import {
  MILES_CHECKOUT_MIN,
  MILES_MIN_PLAN_CENTS,
  checkoutMilesToUse,
  coversWholeTrip as milesPayWholeTrip,
  tripCentsForMiles,
} from '../../../supabase/functions/_shared/miles-redemption.js';
import { MILES_CARD_BACKGROUND, MILES_WORDMARK_STYLE, milesGold } from './milesCardStyles';

const fmtMiles = (n) => Math.max(0, Math.floor(Number(n) || 0)).toLocaleString('en-US');

const EASE_OUT = [0.22, 1, 0.36, 1];

const LINK_GOLD = 'text-[12px] font-normal text-[#f3dca6]/85 hover:text-[#fff3d6] underline underline-offset-[3px] decoration-[#f3dca6]/30 hover:decoration-[#f3dca6]/70 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#f2d9a0]/60 rounded-sm';
const LINK_QUIET = 'text-[12px] font-normal text-white/45 hover:text-white/80 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/40 rounded-sm';

// A gold track and thumb on every engine (the fill is the input's own background).
const RANGE_CLASS = [
  'block h-1.5 w-full cursor-pointer appearance-none rounded-full outline-none',
  '[&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-[#fbeac2] [&::-webkit-slider-thumb]:shadow-[0_0_0_4px_rgba(242,217,160,0.16),0_2px_8px_rgba(0,0,0,0.6)]',
  '[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-[#fbeac2] [&::-moz-range-thumb]:shadow-[0_0_0_4px_rgba(242,217,160,0.16),0_2px_8px_rgba(0,0,0,0.6)]',
  '[&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-transparent',
  'focus-visible:[&::-webkit-slider-thumb]:shadow-[0_0_0_6px_rgba(242,217,160,0.32),0_2px_8px_rgba(0,0,0,0.6)]',
].join(' ');

/** Fine engraved rings spreading from the Miles seal, like the guilloché on a metal card. */
function CardEngraving() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 256 256"
      fill="none"
      className="pointer-events-none absolute -right-[90px] -top-[94px] h-64 w-64 text-[#f2d9a0]"
    >
      {[28, 44, 62, 82, 104, 127].map((r, i) => (
        <circle key={r} cx="128" cy="128" r={r} stroke="currentColor" strokeWidth="0.75" opacity={0.2 - i * 0.03} />
      ))}
    </svg>
  );
}

/**
 * Defera Miles at checkout, shown like a payment method: a gold Miles card
 * with the customer's balance sits above where they add their card, and
 * nothing comes off the price until they choose it. "Apply to trip" uses all
 * the Miles this trip allows; "Choose an amount" (or "Change amount" once
 * applied) lets them type or slide to their own, the way Defera Credit works;
 * "Remove" takes them off.
 *
 * Review decides what a choice comes to (checkoutMilesToUse in
 * miles-redemption.js), and this previews an amount with that same rule, so
 * what it quotes is what applies. It reports the choice: a number of Miles, 0
 * for none, or null for everything allowed. Money is quoted from
 * `valueCentsFor` and `appliedCents`, what the total really falls by, rather
 * than the Miles' face value, so the card and the total never disagree by the
 * hold's rounding cent.
 */
export default function MilesCheckoutPanel({
  milesBalance,
  milesMax,
  milesToUse,
  appliedCents,
  coversWholeTrip = false,
  tripTotalCents,
  allowWholeTrip = true,
  valueCentsFor = tripCentsForMiles,
  unavailableReason = null,
  onChoose,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(0);
  const inputId = useId();
  const reduceMotion = useReducedMotion();
  const balance = Math.floor(Number(milesBalance) || 0);
  if (!(balance > 0)) return null;

  const applied = milesToUse > 0;
  const canApply = milesMax > 0;
  const resolved = editing
    ? checkoutMilesToUse({ requested: draft, balance, tripTotalCents, allowWholeTrip })
    : 0;
  const maxPaysWholeTrip = milesPayWholeTrip(milesMax, tripTotalCents);
  // The payment plan's minimum, not the balance, is what limits the Miles.
  const planKeepsMinimum = canApply && milesMax < balance && !maxPaysWholeTrip;
  // Short of the whole trip, an amount past the plan's limit comes down to it.
  const cappedByPlan = editing && resolved >= MILES_CHECKOUT_MIN && resolved < draft;

  const startEditing = () => {
    setDraft(applied ? milesToUse : milesMax);
    setEditing(true);
  };
  const applyDraft = () => {
    if (resolved < MILES_CHECKOUT_MIN) return;
    onChoose(resolved);
    setEditing(false);
  };
  const setFromInput = (raw) => {
    const typed = parseInt(String(raw).replace(/[^0-9]/g, ''), 10) || 0;
    setDraft(Math.min(typed, milesMax));
  };

  let hint = null;
  if (editing && draft > 0 && draft < MILES_CHECKOUT_MIN) {
    hint = `Use at least ${fmtMiles(MILES_CHECKOUT_MIN)} Miles.`;
  } else if (cappedByPlan) {
    hint = `To pay part of this trip, use up to ${fmtMiles(resolved)} Miles, so $${fmtCurrency(MILES_MIN_PLAN_CENTS)} stays on your plan.`
      + (maxPaysWholeTrip ? ` Or use ${fmtMiles(milesMax)} to pay for all of it.` : '');
  } else if (planKeepsMinimum) {
    hint = `At least $${fmtCurrency(MILES_MIN_PLAN_CENTS)} stays on your payment plan, so up to ${fmtMiles(milesMax)} Miles apply here.`;
  }

  const sliderValue = Math.min(Math.max(draft, MILES_CHECKOUT_MIN), milesMax);
  const sliderPct = milesMax > MILES_CHECKOUT_MIN
    ? ((sliderValue - MILES_CHECKOUT_MIN) / (milesMax - MILES_CHECKOUT_MIN)) * 100
    : 100;
  const usedPct = Math.min(100, (milesToUse / balance) * 100);
  const milesLeft = balance - milesToUse;

  const bigNumber = `text-[30px] min-[400px]:text-[34px] leading-none font-semibold tracking-[-0.02em] tabular-nums pb-0.5 ${milesGold.text}`;

  return (
    <div
      data-testid="miles-checkout-panel"
      className={`relative rounded-2xl p-px ${
        applied
          ? 'bg-gradient-to-br from-[#f6e2b0]/85 via-[#b68a3e]/35 to-[#f6e2b0]/65 shadow-[0_18px_50px_-22px_rgba(233,190,99,0.6)]'
          : 'bg-gradient-to-br from-[#f6e2b0]/45 via-white/[0.06] to-[#c9a25a]/35'
      }`}
    >
      <div className="relative overflow-hidden rounded-[15px] px-5 pb-4 pt-4" style={{ backgroundImage: MILES_CARD_BACKGROUND }}>
        <CardEngraving />
        {applied && !reduceMotion && (
          <motion.span
            key={`shine-${milesToUse}`}
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-[#fff6de]/[0.11] to-transparent"
            initial={{ x: '-120%' }}
            animate={{ x: '260%' }}
            transition={{ duration: 1.3, ease: EASE_OUT }}
          />
        )}

        <div className="relative">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span aria-hidden="true" className="block h-[34px] w-[61px] shrink-0" style={MILES_WORDMARK_STYLE} />
              <span aria-hidden="true" className={`pt-0.5 text-[10px] font-medium uppercase tracking-[0.34em] ${milesGold.ink}`}>Miles</span>
              <span className="sr-only">Defera Miles</span>
            </div>
            <span aria-hidden="true" className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${milesGold.coin}`}>
              <Sparkles className={`h-4 w-4 ${milesGold.coinIcon}`} />
            </span>
          </div>

          {editing ? (
            <div className="mt-5">
              <label htmlFor={inputId} className="block text-[11px] font-medium uppercase tracking-[0.16em] text-white/45">
                Miles to apply
              </label>
              <div className="mt-2 flex items-center gap-3">
                <div className="relative min-w-0 flex-1">
                  <input
                    id={inputId}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    autoFocus
                    value={draft ? fmtMiles(draft) : ''}
                    onChange={(e) => setFromInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); applyDraft(); }
                      if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
                    }}
                    placeholder={fmtMiles(milesMax)}
                    aria-label="Defera Miles to apply to this trip"
                    className="h-12 w-full rounded-xl border border-[#f2d9a0]/20 bg-black/45 pl-4 pr-14 text-[20px] font-semibold text-white tabular-nums outline-none transition-[border-color,box-shadow] placeholder:text-white/25 focus:border-[#f2d9a0]/55 focus:ring-2 focus:ring-[#f2d9a0]/15"
                  />
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[12px] font-normal text-white/40">Miles</span>
                </div>
                <div className="flex shrink-0 flex-col items-end">
                  <span className="text-[11px] font-normal text-white/45">Off this trip</span>
                  <span className="text-[18px] font-semibold tabular-nums text-[#f6e3b4]">
                    {resolved >= MILES_CHECKOUT_MIN ? `$${fmtCurrency(valueCentsFor(resolved))}` : '—'}
                  </span>
                </div>
              </div>

              {milesMax > MILES_CHECKOUT_MIN && (
                <div className="mt-5">
                  <input
                    type="range"
                    min={MILES_CHECKOUT_MIN}
                    max={milesMax}
                    step={1}
                    value={sliderValue}
                    onChange={(e) => setDraft(Number(e.target.value))}
                    aria-label="Defera Miles amount"
                    className={RANGE_CLASS}
                    style={{
                      background: `linear-gradient(to right, #c99a45 0%, #f6e3b4 ${sliderPct}%, rgba(255,255,255,0.1) ${sliderPct}%, rgba(255,255,255,0.1) 100%)`,
                    }}
                  />
                  <div className="mt-2 flex items-center justify-between text-[11px] font-normal tabular-nums text-white/40">
                    <span>{fmtMiles(MILES_CHECKOUT_MIN)}</span>
                    <button type="button" onClick={() => setDraft(milesMax)} className="hover:text-white/75 transition-colors">
                      Max {fmtMiles(milesMax)}
                    </button>
                  </div>
                </div>
              )}

              {hint && <p className="mt-3 text-[12px] font-normal leading-relaxed text-white/55">{hint}</p>}

              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={applyDraft}
                  disabled={resolved < MILES_CHECKOUT_MIN}
                  className={`h-10 rounded-full px-5 text-[13px] font-semibold ${milesGold.button}`}
                >
                  {resolved >= MILES_CHECKOUT_MIN ? `Apply ${fmtMiles(resolved)} Miles` : 'Apply'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="h-10 px-3 text-[13px] font-normal text-white/55 hover:text-white/85 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : applied ? (
            <>
              <div className="mt-5">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                  <p className={bigNumber}>−${fmtCurrency(appliedCents)}</p>
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#f2d9a0]/[0.12] px-2.5 py-1 text-[11px] font-medium text-[#f6e3b4] ring-1 ring-inset ring-[#f2d9a0]/30">
                    <Check className="h-3 w-3" strokeWidth={2.75} aria-hidden="true" />
                    {coversWholeTrip ? 'Pays your whole trip' : 'Applied'}
                  </span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 text-[12px] font-normal tabular-nums">
                  <span className="text-white/65">{`${fmtMiles(milesToUse)} of ${fmtMiles(balance)} Miles applied`}</span>
                  {milesLeft > 0 && <span className="text-white/40">{`${fmtMiles(milesLeft)} left`}</span>}
                </div>
                <div className="mt-2 h-[3px] overflow-hidden rounded-full bg-white/[0.08]">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-[#c99a45] via-[#efcf88] to-[#fff1cf]"
                    initial={reduceMotion ? false : { width: 0 }}
                    animate={{ width: `${usedPct}%` }}
                    transition={{ duration: 0.8, ease: EASE_OUT }}
                  />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#f2d9a0]/[0.12] pt-3.5">
                <button type="button" onClick={startEditing} className={LINK_GOLD}>Change amount</button>
                <button type="button" onClick={() => onChoose(0)} className={LINK_QUIET}>Remove</button>
              </div>
            </>
          ) : (
            <>
              <div className="mt-5 flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
                <div className="min-w-0">
                  <p className={bigNumber}>{fmtMiles(balance)}</p>
                  <p className="mt-2 text-[12px] font-normal text-white/55">available balance</p>
                </div>
                {canApply && (
                  <button
                    type="button"
                    onClick={() => onChoose(null)}
                    className={`h-10 shrink-0 rounded-full px-5 text-[13px] font-semibold ${milesGold.button}`}
                  >
                    Apply to trip
                  </button>
                )}
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-[#f2d9a0]/[0.12] pt-3.5">
                {canApply ? (
                  <>
                    <p className="text-[12px] font-normal text-white/55">
                      Worth up to{' '}
                      <span className="font-medium tabular-nums text-[#f6e3b4]">${fmtCurrency(valueCentsFor(milesMax))}</span>
                      {' '}on this trip
                    </p>
                    <button type="button" onClick={startEditing} className={LINK_GOLD}>Choose an amount</button>
                  </>
                ) : (
                  <p className="text-[12px] font-normal text-white/45">
                    {unavailableReason || (balance < MILES_CHECKOUT_MIN
                      ? `Miles apply from ${fmtMiles(MILES_CHECKOUT_MIN)}.`
                      : 'Miles can’t be applied to this trip.')}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

MilesCheckoutPanel.propTypes = {
  /** The customer's whole Miles balance. */
  milesBalance: PropTypes.number.isRequired,
  /** The most Miles this trip can use (0 when none can be applied). */
  milesMax: PropTypes.number.isRequired,
  /** The Miles this checkout spends now (0 until the customer applies them). */
  milesToUse: PropTypes.number.isRequired,
  /** What the total really fell by, in cents. */
  appliedCents: PropTypes.number.isRequired,
  /** The Miles pay for the whole trip (it's booked with Miles alone). */
  coversWholeTrip: PropTypes.bool,
  /** The trip total before Miles, in cents: the total Review sizes Miles against. */
  tripTotalCents: PropTypes.number.isRequired,
  /** Whether Miles may pay for the whole trip here (Review's rule). */
  allowWholeTrip: PropTypes.bool,
  /** What a number of Miles really takes off this trip, in cents. */
  valueCentsFor: PropTypes.func,
  /** Why Miles can't be applied here, when milesMax is 0 for a reason the customer should know. */
  unavailableReason: PropTypes.string,
  /** Called with a number of Miles, 0 to remove them, or null for all the trip allows. */
  onChoose: PropTypes.func.isRequired,
};
